import { Button } from '../common/Button';
import { GlassPanel } from '../common/GlassPanel';
import type { PairingState } from '../settings/headsetPairingState';

interface PairFormProps {
  code: string;
  deviceName: string;
  isBusy: boolean;
  onChangeCode: (value: string) => void;
  onChangeDeviceName: (value: string) => void;
  onPair: () => void;
}

/**
 * Prominent pairing code input form for the /pair page.
 */
export function PairForm({
  code,
  deviceName,
  isBusy,
  onChangeCode,
  onChangeDeviceName,
  onPair,
}: PairFormProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isBusy) {
      onPair();
    }
  };

  return (
    <GlassPanel variant="strong" padding="lg" className="space-y-5">
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-ink-200">Pairing Code</label>
        <input
          type="text"
          value={code}
          onChange={(e) => onChangeCode(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          placeholder="AB12"
          maxLength={4}
          autoFocus
          className="
            w-full px-4 py-4 bg-white/5 border border-white/15 rounded-xl
            text-white text-center text-3xl font-mono tracking-[0.4em]
            placeholder-white/20
            focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20
            transition-all duration-150
          "
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-ink-200">Device Name</label>
        <input
          type="text"
          value={deviceName}
          onChange={(e) => onChangeDeviceName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Quest Headset"
          maxLength={40}
          className="
            w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl
            text-white placeholder-white/20
            focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20
            transition-all duration-150
          "
        />
      </div>
      <Button
        variant="primary"
        size="lg"
        onClick={onPair}
        disabled={isBusy}
        isLoading={isBusy}
        className="w-full"
      >
        Pair Headset
      </Button>
    </GlassPanel>
  );
}

interface PairFeedbackProps {
  status: PairingState['status'];
  message: string;
}

/**
 * Success or error feedback after pairing attempt.
 */
export function PairFeedback({ status, message }: PairFeedbackProps) {
  if (!message) {
    return null;
  }

  const isError = status === 'error';

  return (
    <GlassPanel
      variant="soft"
      padding="sm"
      className={isError ? 'border border-red-500/30' : 'border border-emerald-500/30'}
    >
      <p className={`text-sm text-center ${isError ? 'text-red-400' : 'text-emerald-300'}`}>
        {message}
      </p>
    </GlassPanel>
  );
}
